import {
  Banknote,
  BookOpen,
  CalendarCheck,
  ClipboardList,
  CalendarDays,
  CalendarRange,
  FileBarChart,
  FolderTree,
  GraduationCap,
  LayoutDashboard,
  Megaphone,
  // MessageSquare,   // used only by the hidden Messages entry below
  ScrollText,
  ShieldCheck,
  Users,
  // Wallet,           // used only by the hidden HR & payroll entry below
  type LucideIcon,
} from 'lucide-react';

/*
  The navigation, as data.

  It used to be an array of objects each holding a pasted <svg> with a 400-
  character `d` attribute, which is why the file was 250 lines to express eight
  links. Icons now come from one set, so they share a stroke weight and an
  optical size — mixing two icon sets is the single most reliable way to make a
  product look assembled rather than designed.

  Grouping matters as much as the icons: eight flat links is a list to read,
  whereas four short groups is a structure to navigate. The labels say what the
  section is FOR, in the words a school office would use.
*/

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Roles allowed to see the item. Omitted means everyone signed in. */
  roles?: string[];
}

export interface NavGroup {
  /** Groups with no label render as a leading block with no heading. */
  label?: string;
  items: NavItem[];
}

/* Every screen but the dismissal board is office work. Listing it once here
   keeps the sidebar honest for a teacher signed in on a classroom screen. */
const ADMIN_ONLY = ['institution_admin', 'super_admin'];

export const NAV: NavGroup[] = [
  {
    items: [{ label: 'Live board', to: '/dashboard', icon: LayoutDashboard, roles: ADMIN_ONLY }],
  },
  {
    label: 'Register',
    items: [
      { label: 'Students', to: '/students', icon: Users, roles: ADMIN_ONLY },
      { label: 'Groups', to: '/groups', icon: FolderTree, roles: ADMIN_ONLY },
    ],
  },
  {
    label: 'Gate',
    items: [
      /* First in the group on purpose: during dismissal it is the only screen
         anybody looks at, and it is the one a teacher is sent to. */
      { label: 'Dismissal board', to: '/dismissal', icon: ClipboardList, roles: ['gate_officer', 'teacher', 'institution_admin', 'super_admin'] },
      /* Teachers too — they are the ones who know whether a child is in the
         room, and the API scopes them to their own classes. */
      { label: 'Attendance', to: '/attendance', icon: CalendarCheck, roles: ['teacher', 'institution_admin', 'super_admin'] },
      { label: 'Audit log', to: '/audit', icon: ScrollText, roles: ADMIN_ONLY },
    ],
  },
  {
    label: 'Academics',
    items: [
      { label: 'Homework', to: '/homework', icon: BookOpen, roles: ADMIN_ONLY },
      /* Teachers too: "My week" is the view that answers "where am I at
         11:00", which no single class grid can. */
      { label: 'Timetable', to: '/timetable', icon: CalendarDays, roles: ['teacher', 'institution_admin', 'super_admin'] },
      /* Teachers too — they mark the papers. The office creates the exams and
         publishes them; the screen hides what a teacher may not do, and the API
         refuses it regardless. */
      { label: 'Exam results', to: '/exams', icon: GraduationCap, roles: ['teacher', 'institution_admin', 'super_admin'] },
    ],
  },
  {
    label: 'Finance',
    items: [{ label: 'Fees', to: '/fees', icon: Banknote, roles: ADMIN_ONLY }],
  },
  {
    label: 'People',
    items: [
      { label: 'Staff', to: '/staff', icon: ShieldCheck, roles: ADMIN_ONLY },
      /* Hidden for now. To restore it, uncomment this line AND the `Wallet`
         import at the top. The route and the page are untouched, so
         /hr-payroll still resolves for anyone who has the link. */
      // { label: 'HR & payroll', to: '/hr-payroll', icon: Wallet, roles: ['institution_admin', 'super_admin'] },
    ],
  },
  {
    label: 'Communication',
    items: [
      { label: 'Notice board', to: '/notices', icon: Megaphone, roles: ADMIN_ONLY },
      /* Hidden for now. To restore it, uncomment this line AND the
         `MessageSquare` import at the top. */
      // { label: 'Messages', to: '/messages', icon: MessageSquare, roles: ADMIN_ONLY },
      { label: 'Events', to: '/events', icon: CalendarRange, roles: ADMIN_ONLY },
    ],
  },
  {
    label: 'Insight',
    items: [{ label: 'Reports', to: '/reports', icon: FileBarChart, roles: ADMIN_ONLY }],
  },
];

/** Page titles and breadcrumb trails, keyed by route, so the bar and the page
 *  cannot disagree about what the screen is called. */
export const ROUTE_TITLES: Record<string, string> = {
  '/dashboard': 'Live board',
  '/students': 'Students',
  '/groups': 'Groups',
  '/attendance': 'Attendance',
  '/dismissal': 'Dismissal board',
  '/staff': 'Staff',
  '/hr-payroll': 'HR & payroll',
  '/audit': 'Audit log',
  '/reports': 'Reports',
  '/homework': 'Homework',
  '/timetable': 'Timetable',
  '/exams': 'Exam results',
  '/exams/:examId': 'Marks',
  '/fees': 'Fees',
  '/notices': 'Notice board',
  '/messages': 'Messages',
  '/events': 'Events',
};
